/**
 * Named salt combinations — save / load / edit / delete.
 * Pretty card grid for Stage 3 Nutrient Salt Management.
 */

import React, { useCallback, useEffect, useState } from 'react';
import {
  Box,
  Card,
  CardContent,
  CardActions,
  Typography,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Chip,
  IconButton,
  Tooltip,
  Alert,
  Stack,
  Divider,
  Fade,
} from '@mui/material';
import {
  BookmarkAdd as SaveIcon,
  PlaylistAddCheck as LoadIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Science as ScienceIcon,
  CollectionsBookmark as CollectionsIcon,
  CheckCircle as CheckIcon,
} from '@mui/icons-material';
import {
  listSaltCombinations,
  saveSaltCombination,
  deleteSaltCombination,
  type SaltCombination,
  type SaltCombinationSnapshot,
} from '../../utils/saltCombinationsStorage';

interface SaltLike {
  id: string;
  name: string;
  formula?: string;
}

interface SaltCombinationsPanelProps {
  /** Currently selected salts (full objects) */
  selectedSalts: SaltLike[];
  /** Apply combination salt IDs to the library checkboxes */
  onLoadCombination: (saltIds: string[], combination: SaltCombination) => void;
  /** Optional: after save, parent can toast */
  onSaved?: (combo: SaltCombination) => void;
}

const SaltCombinationsPanel: React.FC<SaltCombinationsPanelProps> = ({
  selectedSalts,
  onLoadCombination,
  onSaved,
}) => {
  const [combos, setCombos] = useState<SaltCombination[]>([]);
  const [message, setMessage] = useState<{ type: 'success' | 'error' | 'info'; text: string } | null>(
    null
  );
  const [saveOpen, setSaveOpen] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [active, setActive] = useState<SaltCombination | null>(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [error, setError] = useState('');

  const refresh = useCallback(() => {
    setCombos(listSaltCombinations());
  }, []);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const openSave = () => {
    setName('');
    setDescription('');
    setError('');
    setActive(null);
    setSaveOpen(true);
  };

  const openEdit = (c: SaltCombination) => {
    setActive(c);
    setName(c.name);
    setDescription(c.description || '');
    setError('');
    setEditOpen(true);
  };

  const openDelete = (c: SaltCombination) => {
    setActive(c);
    setDeleteOpen(true);
  };

  const snapshotsFromSelection = (): SaltCombinationSnapshot[] =>
    selectedSalts.map((s) => ({
      id: s.id,
      name: s.name,
      formula: s.formula,
    }));

  const handleSaveNew = () => {
    try {
      setError('');
      const combo = saveSaltCombination({
        name,
        description,
        saltIds: selectedSalts.map((s) => s.id),
        saltSnapshots: snapshotsFromSelection(),
      });
      setSaveOpen(false);
      refresh();
      setMessage({ type: 'success', text: `Saved combination “${combo.name}”` });
      onSaved?.(combo);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not save');
    }
  };

  const handleUpdateMeta = () => {
    if (!active) return;
    try {
      setError('');
      // Keep salt set; only rename/description — or replace salts with current selection if user wants
      const combo = saveSaltCombination({
        id: active.id,
        name,
        description,
        saltIds: selectedSalts.length > 0 ? selectedSalts.map((s) => s.id) : active.saltIds,
        saltSnapshots:
          selectedSalts.length > 0
            ? snapshotsFromSelection()
            : active.saltSnapshots,
      });
      setEditOpen(false);
      refresh();
      setMessage({
        type: 'success',
        text:
          selectedSalts.length > 0
            ? `Updated “${combo.name}” (name + current selection)`
            : `Renamed combination “${combo.name}”`,
      });
      onSaved?.(combo);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Could not update');
    }
  };

  const handleDelete = () => {
    if (!active) return;
    deleteSaltCombination(active.id);
    setDeleteOpen(false);
    setActive(null);
    refresh();
    setMessage({ type: 'info', text: `Deleted “${active.name}”` });
  };

  const handleLoad = (c: SaltCombination) => {
    onLoadCombination(c.saltIds, c);
    setMessage({
      type: 'success',
      text: `Loaded “${c.name}” (${c.saltIds.length} salts)`,
    });
  };

  return (
    <Card
      elevation={0}
      sx={{
        mb: 3,
        border: '1px solid',
        borderColor: 'divider',
        borderRadius: 2,
        background: 'linear-gradient(135deg, #f0f7ff 0%, #faf5ff 50%, #f8fafc 100%)',
      }}
    >
      <CardContent>
        <Box
          sx={{
            display: 'flex',
            flexWrap: 'wrap',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 2,
            mb: 2,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                width: 44,
                height: 44,
                borderRadius: 2,
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
              }}
            >
              <CollectionsIcon />
            </Box>
            <Box>
              <Typography variant="h6" fontWeight={700}>
                Salt combinations
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Save a named set of salts, load it anytime, edit or delete
              </Typography>
            </Box>
          </Box>

          <Tooltip
            title={
              selectedSalts.length === 0
                ? 'Select salts in the library first'
                : 'Save current selection as a named combination'
            }
          >
            <span>
              <Button
                variant="contained"
                startIcon={<SaveIcon />}
                onClick={openSave}
                disabled={selectedSalts.length === 0}
                sx={{ borderRadius: 2, textTransform: 'none', px: 2.5 }}
              >
                Save selection ({selectedSalts.length})
              </Button>
            </span>
          </Tooltip>
        </Box>

        {message && (
          <Fade in>
            <Alert
              severity={message.type}
              sx={{ mb: 2, borderRadius: 2 }}
              onClose={() => setMessage(null)}
            >
              {message.text}
            </Alert>
          </Fade>
        )}

        {combos.length === 0 ? (
          <Box
            sx={{
              py: 4,
              px: 2,
              textAlign: 'center',
              borderRadius: 2,
              border: '1px dashed',
              borderColor: 'divider',
              bgcolor: 'background.paper',
            }}
          >
            <ScienceIcon sx={{ fontSize: 40, color: 'text.disabled', mb: 1 }} />
            <Typography variant="subtitle1" fontWeight={600} gutterBottom>
              No saved combinations yet
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ maxWidth: 420, mx: 'auto' }}>
              Select salts in the library (or a whole group), then click{' '}
              <strong>Save selection</strong> and give it a name — e.g. “Tomato full set” or
              “Nitrogen pack”.
            </Typography>
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: {
                xs: '1fr',
                sm: '1fr 1fr',
                md: '1fr 1fr 1fr',
              },
              gap: 2,
            }}
          >
            {combos.map((c) => (
              <Card
                key={c.id}
                variant="outlined"
                sx={{
                  borderRadius: 2,
                  height: '100%',
                  display: 'flex',
                  flexDirection: 'column',
                  transition: 'box-shadow 0.2s, transform 0.15s',
                  '&:hover': {
                    boxShadow: 4,
                    transform: 'translateY(-2px)',
                  },
                }}
              >
                <CardContent sx={{ flex: 1 }}>
                  <Stack direction="row" justifyContent="space-between" alignItems="flex-start">
                    <Typography variant="subtitle1" fontWeight={700} gutterBottom>
                      {c.name}
                    </Typography>
                    <Chip
                      size="small"
                      color="primary"
                      variant="outlined"
                      label={`${c.saltIds.length} salts`}
                    />
                  </Stack>
                  {c.description && (
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                      {c.description}
                    </Typography>
                  )}
                  <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                    {(c.saltSnapshots.length > 0
                      ? c.saltSnapshots
                      : c.saltIds.map((id) => ({ id, name: id }))
                    )
                      .slice(0, 6)
                      .map((s) => (
                        <Chip
                          key={s.id}
                          size="small"
                          label={s.name}
                          sx={{ maxWidth: 140 }}
                        />
                      ))}
                    {c.saltIds.length > 6 && (
                      <Chip size="small" label={`+${c.saltIds.length - 6}`} variant="outlined" />
                    )}
                  </Box>
                  <Typography
                    variant="caption"
                    color="text.disabled"
                    display="block"
                    sx={{ mt: 1.5 }}
                  >
                    Updated {new Date(c.updatedAt).toLocaleString()}
                  </Typography>
                </CardContent>
                <Divider />
                <CardActions sx={{ px: 1.5, py: 1, justifyContent: 'space-between' }}>
                  <Button
                    size="small"
                    color="primary"
                    startIcon={<LoadIcon />}
                    onClick={() => handleLoad(c)}
                    sx={{ textTransform: 'none', fontWeight: 600 }}
                  >
                    Use this set
                  </Button>
                  <Box>
                    <Tooltip title="Edit name / replace with current selection">
                      <IconButton size="small" onClick={() => openEdit(c)} aria-label="Edit">
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="Delete combination">
                      <IconButton
                        size="small"
                        color="error"
                        onClick={() => openDelete(c)}
                        aria-label="Delete"
                      >
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </CardActions>
              </Card>
            ))}
          </Box>
        )}
      </CardContent>

      {/* Save dialog */}
      <Dialog open={saveOpen} onClose={() => setSaveOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Save salt combination</DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {selectedSalts.length} salt(s) will be saved. You can load this set later without
            re-selecting.
          </Typography>
          <TextField
            autoFocus
            fullWidth
            label="Combination name"
            placeholder="e.g. Tomato till first cluster"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Notes (optional)"
            placeholder="Crop, notes, CF…"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
          <Box sx={{ mt: 2, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {selectedSalts.map((s) => (
              <Chip key={s.id} size="small" icon={<CheckIcon />} label={s.name} />
            ))}
          </Box>
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setSaveOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveNew} disabled={!name.trim()}>
            Save combination
          </Button>
        </DialogActions>
      </Dialog>

      {/* Edit dialog */}
      <Dialog open={editOpen} onClose={() => setEditOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ fontWeight: 700 }}>Edit combination</DialogTitle>
        <DialogContent>
          <Alert severity="info" sx={{ mb: 2 }}>
            Change the name/notes. If you currently have salts selected in the library, saving
            will <strong>replace</strong> this combination’s salts with your current selection.
            Load the set first, adjust checkboxes, then edit &amp; save.
          </Alert>
          <TextField
            autoFocus
            fullWidth
            label="Combination name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            sx={{ mb: 2 }}
          />
          <TextField
            fullWidth
            label="Notes (optional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            multiline
            minRows={2}
          />
          {selectedSalts.length > 0 && (
            <Typography variant="caption" color="primary" display="block" sx={{ mt: 1 }}>
              Will update salt list to current selection ({selectedSalts.length} salts).
            </Typography>
          )}
          {error && (
            <Alert severity="error" sx={{ mt: 2 }}>
              {error}
            </Alert>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2 }}>
          <Button onClick={() => setEditOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleUpdateMeta} disabled={!name.trim()}>
            Save changes
          </Button>
        </DialogActions>
      </Dialog>

      {/* Delete confirm */}
      <Dialog open={deleteOpen} onClose={() => setDeleteOpen(false)} maxWidth="xs" fullWidth>
        <DialogTitle>Delete combination?</DialogTitle>
        <DialogContent>
          <Typography variant="body2">
            Remove <strong>{active?.name}</strong>? This only deletes the saved set — library
            salts stay in the database.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteOpen(false)}>Cancel</Button>
          <Button color="error" variant="contained" onClick={handleDelete}>
            Delete
          </Button>
        </DialogActions>
      </Dialog>
    </Card>
  );
};

export default SaltCombinationsPanel;
